'use client';

import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface StockPhoto {
  id: string;
  url: string;
  thumbnail: string;
  width: number;
  height: number;
  photographer: string;
  source: 'pexels' | 'pixabay' | 'unsplash';
  downloadUrl?: string;
}

interface ImageSearchProps {
  onImageSelected: (imageUrl: string, fileName: string) => void;
  locale?: string;
}

export function ImageSearch({ onImageSelected, locale = 'nl' }: ImageSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState<StockPhoto[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedImage, setSelectedImage] = useState<StockPhoto | null>(null);
  const [providers, setProviders] = useState<{ pexels: boolean; pixabay: boolean; unsplash: boolean }>({
    pexels: false,
    pixabay: false,
    unsplash: false,
  });
  const copy = locale === 'de'
    ? {
        placeholder: 'Kostenlose Bilder suchen (Pexels, Pixabay, Unsplash)...',
        searchFailed: 'Suche fehlgeschlagen',
        noImages: 'Keine Bilder gefunden',
        searchError: 'Fehler bei der Bildsuche',
        selected: 'Bild ausgewählt',
        searchingIn: 'Suchen in:',
        noKeys: 'Keine API-Schlüssel konfiguriert',
        photoBy: 'Foto von',
        providedBy: 'Fotos bereitgestellt von:',
      }
    : locale === 'fr'
      ? {
          placeholder: 'Rechercher des images gratuites (Pexels, Pixabay, Unsplash)...',
          searchFailed: 'Recherche échouée',
          noImages: 'Aucune image trouvée',
          searchError: 'Erreur lors de la recherche d’images',
          selected: 'Image sélectionnée',
          searchingIn: 'Recherche dans :',
          noKeys: 'Aucune clé API configurée',
          photoBy: 'Photo par',
          providedBy: 'Photos fournies par :',
        }
      : locale === 'en'
        ? {
            placeholder: 'Search free images (Pexels, Pixabay, Unsplash)...',
            searchFailed: 'Search failed',
            noImages: 'No images found',
            searchError: 'Error searching images',
            selected: 'Image selected',
            searchingIn: 'Searching in:',
            noKeys: 'No API keys configured',
            photoBy: 'Photo by',
            providedBy: 'Photos provided by:',
          }
        : {
            placeholder: 'Zoek gratis afbeeldingen (Pexels, Pixabay, Unsplash)...',
            searchFailed: 'Zoeken mislukt',
            noImages: 'Geen afbeeldingen gevonden',
            searchError: 'Fout bij zoeken naar afbeeldingen',
            selected: 'Afbeelding geselecteerd',
            searchingIn: 'Zoeken in:',
            noKeys: 'Geen API keys geconfigureerd',
            photoBy: 'Foto door',
            providedBy: "Foto's aangeboden door:",
          };

  // Debounced search effect
  const performSearch = async (query: string) => {
    try {
      const response = await fetch(
        `/api/stock-photos?q=${encodeURIComponent(query)}&per_page=20`
      );

      if (!response.ok) {
        throw new Error(copy.searchFailed);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      setImages(data.photos || []);
      setProviders(data.providers || { pexels: false, pixabay: false, unsplash: false });

      if (data.photos.length === 0) {
        toast.info(copy.noImages);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(copy.searchError);
      setImages([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setImages([]);
      return;
    }

    setSearching(true);

    // Debounce delay
    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);


  const handleSelectImage = (image: StockPhoto) => {
    setSelectedImage(image);
    // Use download URL if available, otherwise use regular URL
    const imageUrl = image.downloadUrl || image.url;
    const fileName = `${image.source}-${image.id}.jpg`;
    onImageSelected(imageUrl, fileName);
    toast.success(copy.selected);
  };

  // Get provider badge color
  const getProviderBadgeColor = (source: string) => {
    switch (source) {
      case 'pexels':
        return 'bg-green-500';
      case 'pixabay':
        return 'bg-blue-500';
      case 'unsplash':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Input - Live search as you type */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {searching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </div>
        <Input
          type="text"
          placeholder={copy.placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Active Providers Info */}
      {searchQuery && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>{copy.searchingIn}</span>
          {providers.pexels && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Pexels
            </span>
          )}
          {providers.pixabay && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Pixabay
            </span>
          )}
          {providers.unsplash && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
              Unsplash
            </span>
          )}
          {!providers.pexels && !providers.pixabay && !providers.unsplash && (
            <span className="text-orange-600">{copy.noKeys}</span>
          )}
        </div>
      )}

      {/* Results Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                selectedImage?.id === image.id
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-transparent hover:border-blue-300'
              }`}
              onClick={() => handleSelectImage(image)}
            >
              <div className="aspect-square relative">
                <img
                  src={image.thumbnail}
                  alt={`Door ${image.photographer}`}
                  className="w-full h-full object-cover"
                />

                {/* Provider Badge */}
                <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium text-white ${getProviderBadgeColor(image.source)}`}>
                  {image.source.charAt(0).toUpperCase() + image.source.slice(1)}
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                  <Download className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Photographer Credit */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                <p className="text-xs text-white truncate">
                  {copy.photoBy} {image.photographer}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Attribution */}
      {images.length > 0 && (
        <div className="text-xs text-gray-500 text-center space-y-1">
          <p>{copy.providedBy}</p>
          <div className="flex items-center justify-center gap-4">
            {providers.pexels && (
              <a
                href="https://www.pexels.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-green-600"
              >
                Pexels
              </a>
            )}
            {providers.pixabay && (
              <a
                href="https://pixabay.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-blue-600"
              >
                Pixabay
              </a>
            )}
            {providers.unsplash && (
              <a
                href="https://unsplash.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-purple-600"
              >
                Unsplash
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
